import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Building2, Layers, Home } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBuildingConfig } from '../../lib/hooks/useBuildingConfig';
import { useApartments } from '../../lib/hooks/useApartments';
import FloorPlansViewer from '../FloorPlansViewer';

// Step-by-step navigator: entrance → floor → unit. Each step narrows the
// choice, so the visitor is never shown a plan before they picked where they
// are. The final step reuses FloorPlansViewer in controlled mode.

type StepKey = 'entrance' | 'floor' | 'unit';

const NavigatorWizard = () => {
  const navigate = useNavigate();
  const { config, getAvailableFloors } = useBuildingConfig();
  const { entrance: entranceParam, floor: floorParam } = useParams();
  const { apartments } = useApartments();

  // A single-entrance building has nothing to choose in step 1 — skip it.
  const entrances = config.entrances || [];
  const skipEntrance = entrances.length <= 1;

  const [entrance, setEntrance] = useState<string>(entrances[0]?.id ?? '1');
  const [floor, setFloor] = useState<number | null>(null);
  const [step, setStep] = useState<StepKey>(skipEntrance ? 'floor' : 'entrance');

  // Arriving from a facade click lands directly on the plan.
  useEffect(() => {
    if (entranceParam) setEntrance(entranceParam);
    if (floorParam) {
      setFloor(Number(floorParam));
      setStep('unit');
    }
  }, [entranceParam, floorParam]);

  // The building config loads asynchronously, so the initial step is computed
  // against the default config. Correct it once the real entrances arrive.
  useEffect(() => {
    if (!skipEntrance) return;
    if (entrances[0]) setEntrance(entrances[0].id);
    setStep((prev) => (prev === 'entrance' ? 'floor' : prev));
  }, [skipEntrance, entrances]);

  const floors = getAvailableFloors(entrance);

  // Free-unit counts per floor, so the visitor can see where availability is
  // before opening a plan.
  const freeByFloor = useMemo(() => {
    const map: Record<number, number> = {};
    apartments
      .filter((a) => a.entrance === entrance && a.status === 'available')
      .forEach((a) => {
        map[a.floor] = (map[a.floor] || 0) + 1;
      });
    return map;
  }, [apartments, entrance]);

  const totalByFloor = useMemo(() => {
    const map: Record<number, number> = {};
    apartments
      .filter((a) => a.entrance === entrance)
      .forEach((a) => {
        map[a.floor] = (map[a.floor] || 0) + 1;
      });
    return map;
  }, [apartments, entrance]);

  const steps: { key: StepKey; label: string; icon: typeof Building2 }[] = [
    ...(skipEntrance ? [] : [{ key: 'entrance' as StepKey, label: 'Вход', icon: Building2 }]),
    { key: 'floor', label: 'Етаж', icon: Layers },
    { key: 'unit', label: 'Обект', icon: Home },
  ];
  const currentIndex = steps.findIndex((s) => s.key === step);

  const goTo = (key: StepKey) => {
    const idx = steps.findIndex((s) => s.key === key);
    // Never jump forward past a step that has no answer yet.
    if (key === 'unit' && floor === null) return;
    if (idx <= currentIndex || (key === 'floor' && entrance)) setStep(key);
  };

  const entranceName =
    entrances.find((e) => e.id === entrance)?.name || `Вход ${entrance}`;

  return (
    <div className="min-h-screen bg-neutral-50 pt-24 pb-16">
      <div className="container-custom">
        <h1 className="section-title text-center mb-2">Изберете обект</h1>
        <p className="text-center text-neutral-500 mb-10">
          Следвайте стъпките, за да стигнете до конкретния апартамент или офис.
        </p>

        {/* Progress bar */}
        <ol className="flex items-center justify-center mb-12">
          {steps.map((s, i) => {
            const done = i < currentIndex;
            const active = i === currentIndex;
            const Icon = s.icon;
            return (
              <li key={s.key} className="flex items-center">
                <button
                  onClick={() => goTo(s.key)}
                  disabled={i > currentIndex}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors ${
                    active
                      ? 'bg-primary text-white'
                      : done
                      ? 'text-primary hover:bg-primary/10'
                      : 'text-neutral-400 cursor-not-allowed'
                  }`}
                >
                  <span
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                      active
                        ? 'bg-white/20'
                        : done
                        ? 'bg-primary/10'
                        : 'bg-neutral-200 text-neutral-500'
                    }`}
                  >
                    {done ? <Check className="w-4 h-4" /> : i + 1}
                  </span>
                  <span className="hidden sm:inline font-medium">{s.label}</span>
                  <Icon className="w-4 h-4 sm:hidden" />
                </button>
                {i < steps.length - 1 && (
                  <span
                    className={`w-8 sm:w-16 h-px mx-1 ${
                      i < currentIndex ? 'bg-primary' : 'bg-neutral-300'
                    }`}
                  />
                )}
              </li>
            );
          })}
        </ol>

        {/* Step 1 — entrance */}
        {step === 'entrance' && (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold text-center mb-6">Изберете вход</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {entrances.map((e) => (
                <button
                  key={e.id}
                  onClick={() => {
                    setEntrance(e.id);
                    setFloor(null);
                    setStep('floor');
                  }}
                  className={`p-6 rounded-xl border-2 bg-white text-left transition-all hover:shadow-lg ${
                    entrance === e.id
                      ? 'border-primary shadow-lg'
                      : 'border-transparent shadow'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{e.name}</p>
                      <p className="text-sm text-neutral-500 mt-1">
                        {(e.floors || []).length} етажа
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-primary" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — floor */}
        {step === 'floor' && (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold text-center mb-2">Изберете етаж</h2>
            {!skipEntrance && (
              <p className="text-center text-neutral-500 mb-6">{entranceName}</p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {floors.map((f) => {
                const free = freeByFloor[f] || 0;
                const total = totalByFloor[f] || 0;
                return (
                  <button
                    key={f}
                    onClick={() => {
                      setFloor(f);
                      setStep('unit');
                    }}
                    className={`p-4 rounded-xl bg-white border-2 transition-all hover:shadow-lg ${
                      floor === f ? 'border-primary shadow-lg' : 'border-transparent shadow'
                    }`}
                  >
                    <p className="text-2xl font-bold text-gray-900">Етаж {f}</p>
                    {total > 0 ? (
                      <p
                        className={`text-sm mt-1 font-medium ${
                          free > 0 ? 'text-green-600' : 'text-neutral-400'
                        }`}
                      >
                        {free > 0 ? `${free} свободни` : 'Няма свободни'}
                      </p>
                    ) : (
                      <p className="text-sm mt-1 text-neutral-400">&nbsp;</p>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-center mt-8">
              {!skipEntrance && (
                <button
                  onClick={() => setStep('entrance')}
                  className="inline-flex items-center text-primary hover:text-primary-dark"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Назад към входовете
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3 — unit on the floor plan */}
        {step === 'unit' && floor !== null && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <button
                onClick={() => setStep('floor')}
                className="inline-flex items-center text-primary hover:text-primary-dark"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Назад към етажите
              </button>
              <p className="text-neutral-600">
                {!skipEntrance && <span>{entranceName} · </span>}
                <span className="font-semibold text-gray-900">Етаж {floor}</span>
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
              <FloorPlansViewer
                entrance={entrance}
                floor={floor}
                showSelectors={false}
                showTitle={false}
                compact
              />
            </div>
            <div className="text-center mt-8">
              <button
                onClick={() => navigate('/apartments')}
                className="text-primary hover:text-primary-dark font-medium"
              >
                Или разгледайте всички обекти в списък →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavigatorWizard;
